/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:04]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    DropDown,
    Form,
    Modal,
    requiredRule,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { getSelectList } from 'axios/brandApi';
import { getTranslateTypes } from 'axios/translatorApi';
import useApi from 'hooks/useApi';
import { TranslationWorkplace } from 'models/translateWorkplace';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useTranslateWorkplaceState } from './Translate';
import './Translate.scss';

type TranslateChangeModalProps = {
    isOpen?: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

type TransalationItemForm = Omit<TranslationWorkplace, 'type' | 'brandCode'> & {
    type?: string;
    brandCode?: string;
};

export default function TranslateChangeModal({
    isOpen = false,
    toggle,
}: TranslateChangeModalProps) {
    const api = useApi();
    const { t } = useTranslation();
    const { cultures } = useGeneralSettingsContext();
    const [translateCodeList, setList] = useState<DropDown[]>([]);
    const [brandList, setBrandList] = useState<DropDown[]>([]);

    const initialItem: TranslationWorkplace = {
        brandCode: null,
        type: null,
        code: '',
        name: '',
        dictCode: '',
        dictItemCode: '',
        translations: cultures.reduce((a, v) => ({ ...a, [v.code]: '' }), {}),
    };

    const { currentItem, changeItemMutation, addItemMutation } = useTranslateWorkplaceState();

    const [mutableTranslatorItem, changeTraslatorItem] = useState(currentItem || initialItem);

    useEffect(() => changeTraslatorItem(currentItem || initialItem), [currentItem]);

    const getTransBrandsQuery = useQuery('brandList', () => getSelectList(api), {
        onSuccess: (data) => {
            setBrandList(data);
        },
        enabled: !brandList.length,
    });

    const getTransTypesQuery = useQuery('translateCodeList', () => getTranslateTypes(api), {
        onSuccess: (data) => {
            setList(data.map((x) => ({ id: x, name: x })));
        },
        enabled: !translateCodeList.length,
    });

    const saveChanges = (data: TransalationItemForm) => {
        (mutableTranslatorItem?.id ? changeItemMutation : addItemMutation).mutate({
            ...data,
            type: translateCodeList.find((i) => i.id === data.type)?.id?.toString() ?? null,
            brandCode: brandList.find((i) => i.id === data.brandCode)?.id?.toString() ?? null,
        });
        toggle(false);
    };

    const closeModal = () => {
        toggle(false);
    };

    return (
        <Modal size="lg" isOpen={isOpen} toggle={closeModal} className="translator__modal">
            <Form<TransalationItemForm>
                onSubmit={(data) => saveChanges(data)}
                defaultValues={{
                    ...mutableTranslatorItem,
                    type: mutableTranslatorItem.type || undefined,
                    brandCode: mutableTranslatorItem.brandCode || undefined,
                }}
            >
                <ModalHeader toggle={closeModal}>
                    {currentItem
                        ? `${mutableTranslatorItem.type}.${mutableTranslatorItem.name}`
                        : t('BackOfficeNew')}
                </ModalHeader>

                <ModalBody className="translator__modal-body">
                    <Row>
                        <Col>
                            <ValidatedDropDownList<TransalationItemForm>
                                name="brandCode"
                                label={t('BackOfficeBrandName')}
                                disabled={!!currentItem}
                                items={brandList}
                                isLoading={getTransBrandsQuery.isLoading}
                            />
                        </Col>
                        <Col>
                            <ValidatedDropDownList<TransalationItemForm>
                                name="type"
                                label={t('BackOfficeTranslateGroup')}
                                disabled={!!currentItem}
                                items={translateCodeList}
                                isLoading={getTransTypesQuery.isLoading}
                                rules={{ required: requiredRule }}
                            />
                        </Col>
                        <Col>
                            <ValidatedInput<TransalationItemForm>
                                name="name"
                                label={t('BackOfficeTranslateCode')}
                                disabled={!!currentItem}
                                rules={{ required: requiredRule }}
                            />
                        </Col>
                    </Row>
                    {cultures &&
                        cultures.map((x) => {
                            return (
                                <ValidatedInput<TransalationItemForm>
                                    key={x.code}
                                    name={`translations.${x.code}`}
                                    label={x.name}
                                    rules={{ required: requiredRule }}
                                />
                            );
                        })}
                </ModalBody>

                <ModalFooter>
                    <Button type="button" color="secondary" onClick={closeModal}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button color="success" type="submit">
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
}
